import React, { forwardRef, ReactNode, useCallback, useMemo } from "react";
import Picker from "../picker-v2";
import type { PickerProps, PickerRef, PickerActions, PickerValue, PickerColumn } from "../picker";
import { NativeProps, withNativeProps } from "@/utils/components-utils/native-props";
import { mergeProps } from "@/utils/components-utils/with-default-props";
import {
	convertDateToStringArray,
	convertStringArrayToDate,
	generateDatePickerColumns,
	defaultRenderLabel,
} from "./date-picker-utils";
import type { Precision, DatePickerFilter } from "./date-picker-utils";
import { bound } from "@/utils/components-utils/bound";
import useMemoizedFn from "@/hooks/useMemoizedFn";
import { usePropsValue } from "@/hooks/usePropsValue";
import { useRouter } from "next/router";
import { DateZhLocale, MouthEnMap } from "./date-picker-date-utils";
import { useTranslation } from "next-i18next";

export type DatePickerRef = PickerRef;

export type DatePickerProps = Pick<
	PickerProps,
	| "onCancel"
	| "onClose"
	| "closeOnMaskClick"
	| "visible"
	| "confirmText"
	| "cancelText"
	| "getContainer"
	| "loading"
	| "loadingContent"
	| "afterShow"
	| "afterClose"
	| "onClick"
	| "title"
	| "stopPropagation"
	| "style"
	| "mouseWheel"
	| "forceRender"
	| "destroyOnClose"
> & {
	value?: Date | null;
	defaultValue?: Date | null;
	onSelect?: (value: Date) => void;
	onConfirm?: (value: Date) => void;
	onFocus?: () => void;
	min?: Date;
	max?: Date;
	precision?: Precision;
	children?: (value: Date | null, actions: PickerActions) => ReactNode;
	renderLabel?: (type: Precision, data: number) => ReactNode;
	filter?: DatePickerFilter;
} & NativeProps;

const thisYear = new Date().getFullYear();

const defaultProps = {
	min: new Date(new Date().setFullYear(thisYear - 10)),
	max: new Date(new Date().setFullYear(thisYear + 10)),
	precision: "day",
	renderLabel: defaultRenderLabel,
	defaultValue: null as Date | null,
};

export const DatePicker = forwardRef<DatePickerRef, DatePickerProps>((p, ref) => {
	const { t } = useTranslation("common");
	const props = mergeProps(defaultProps, p);
	const router = useRouter();
	const isZhHans = router.locale === "zh-Hans";

	const [value, setValue] = usePropsValue<Date | null>({
		value: props.value,
		defaultValue: props.defaultValue,
		onChange: (v) => {
			if (v === null) return;
			props.onConfirm?.(v);
		},
	});

	const now = useMemo(() => new Date(), []);

	const pickerValue = useMemo(() => {
		let date = value ?? now;
		date = new Date(bound(date.getTime(), props.min.getTime(), props.max.getTime()));
		const _convertDate = convertDateToStringArray(date, props.precision);
		return isZhHans ? _convertDate : [_convertDate[1], _convertDate[2], _convertDate[0]];
	}, [value, props.precision, props.min, props.max, isZhHans]);

	const onConfirm = useCallback(
		(val: PickerValue[]) => {
			const _val = isZhHans ? val : [val[2], val[0], val[1]];
			const date = convertStringArrayToDate(_val, props.precision);
			setValue(date, true);
		},
		[setValue, props.precision, isZhHans],
	);

	const onSelect = useMemoizedFn((val: PickerValue[]) => {
		const _val = isZhHans ? val : [val[2], val[0], val[1]];
		const date = convertStringArrayToDate(_val, props.precision);
		props.onSelect?.(date);
	});

	const renderLabel = (type: Precision, data: number) => {
		const zh_Hans = router.locale === "zh-Hans";
		return (
			<div>
				{!zh_Hans ? (type === "month" ? t(MouthEnMap.get(data) || "") : data) : data}
				{zh_Hans && DateZhLocale.get(type)}
			</div>
		);
	};

	const columns = useCallback<(value: PickerValue[]) => PickerColumn[]>(
		(selected) => {
			// 统一使用 [year, month, day] 的顺序进行内部处理
			const _selected = isZhHans ? selected : [selected[2], selected[0], selected[1]];

			const _column = generateDatePickerColumns(
				_selected as string[],
				props.min,
				props.max,
				props.precision,
				renderLabel,
				props.filter,
				router.locale,
			);
			// 在返回时再转换为显示顺序
			return isZhHans ? _column : [_column[1], _column[2], _column[0]];
		},
		[props.min, props.max, props.precision, isZhHans, renderLabel, props.filter, router.locale],
	);

	return withNativeProps(
		props,
		<Picker
			ref={ref}
			columns={columns}
			value={pickerValue}
			onCancel={props.onCancel}
			onClose={props.onClose}
			closeOnMaskClick={props.closeOnMaskClick}
			visible={props.visible}
			confirmText={props.confirmText}
			cancelText={props.cancelText}
			onConfirm={onConfirm}
			onSelect={onSelect}
			getContainer={props.getContainer}
			loading={props.loading}
			loadingContent={props.loadingContent}
			afterShow={props.afterShow}
			afterClose={props.afterClose}
			onClick={props.onClick}
			title={props.title}
			stopPropagation={props.stopPropagation}
			mouseWheel={props.mouseWheel}
			destroyOnClose={props.destroyOnClose}
			forceRender={props.forceRender}
		>
			{(_, actions) => props.children?.(value, actions)}
		</Picker>,
	);
});

DatePicker.displayName = "DatePicker";
